import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { Add24Regular, Add16Regular, DismissRegular, Edit16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Concept } from '../../../models/Entities/Contracts/Concept';
import { ConceptCategory } from '../../../models/Entities/Contracts/ConceptCategory';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { ContractNegotiationVersion } from '../../../models/Entities/Contracts/ContractNegotiationVersion';
import { ContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/ContractNegotiationVersionConcept';
import { NewContractNegotiationVersion } from '../../../models/Entities/Contracts/NewContractNegotiationVersion';
import { NewContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/NewContractNegotiationVersionConcept';
import VersionConceptCellConnected from './VersionConceptCell.Component';
import { NegotiationConceptGraphComponent } from '../ContractNegotiationGraph/NegotiationConceptGraph.Component';
import {
  formatCurrency,
  formatDate,
  mapDispatchToProps,
  mapStateToProps,
  OwnProps,
  PendingConceptValue,
  Props,
  VersionConceptInputs,
} from './NegotiationConceptGrid.Functions';
import { useNegotiationConceptGridStyles } from './NegotiationConceptGrid.Styles';
import { CurrencySelectorComponent } from '../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';

const UNCATEGORIZED_KEY = '__uncategorized__';

interface CategoryGroup {
  categoryId: number | null;
  categoryName: string;
  concepts: ContractNegotiationConcept[];
}

const buildCategoryGroups = (
  negotiationConcepts: ContractNegotiationConcept[],
  allConcepts: Concept[],
  conceptCategories: ConceptCategory[],
  t: (key: string, fallback: string) => string
): CategoryGroup[] => {
  const conceptMap = new Map<number, Concept>(allConcepts.map((c) => [c.id, c]));
  const categoryMap = new Map<number, ConceptCategory>(
    conceptCategories.map((cat) => [cat.id, cat])
  );

  const groupsMap = new Map<number | typeof UNCATEGORIZED_KEY, CategoryGroup>();

  for (const nc of negotiationConcepts) {
    const concept = conceptMap.get(nc.conceptId);
    const categoryId = concept?.conceptCategoryId ?? null;
    const key: number | typeof UNCATEGORIZED_KEY = categoryId ?? UNCATEGORIZED_KEY;

    if (!groupsMap.has(key)) {
      const categoryName =
        categoryId !== null && categoryId !== undefined
          ? categoryMap.get(categoryId)?.conceptCategoryName ??
            t('negotiationConceptGrid.unknownCategory', 'Sin categoría')
          : t('negotiationConceptGrid.uncategorized', 'Sin categoría');

      groupsMap.set(key, {
        categoryId: categoryId,
        categoryName,
        concepts: [],
      });
    }
    groupsMap.get(key)?.concepts.push(nc);
  }

  return Array.from(groupsMap.values());
};

const getVersionConcept = (
  version: ContractNegotiationVersion,
  negotiationConceptId: string
): ContractNegotiationVersionConcept | undefined =>
  version.concepts?.find((vc) => vc.contractNegotiationConceptId === negotiationConceptId);

type FinalValueResult =
  | { kind: 'siteCost';  value: number; label: string; status: number }
  | { kind: 'sponsorCost'; value: number; label: string; status: number }
  | { kind: 'reference'; value: number; label: string }
  | { kind: 'noData' };

const getFinalValue = (
  versions: ContractNegotiationVersion[],
  negotiationConceptId: string,
  referenceCost: number
): FinalValueResult => {
  if (versions.length === 0) return { kind: 'reference', value: referenceCost, label: 'Referencia' };
  // Find last version that has data for this concept
  for (let i = versions.length - 1; i >= 0; i--) {
    const vc = getVersionConcept(versions[i], negotiationConceptId);
    if (!vc) continue;
    const s = vc.negotiationConceptStatus;
    if (s === 2) {
      // If site made no counter-offer (siteCost null or 0), accepted the sponsor's offer as-is
      const accepted = vc.siteCost ? vc.siteCost : vc.sponsorCost;
      const kind = vc.siteCost ? 'siteCost' : 'sponsorCost';
      return { kind, value: accepted, label: 'Aceptado', status: 2 };
    }
    if (s === 3) return { kind: 'sponsorCost', value: vc.sponsorCost, label: 'Rechazado',  status: 3 };
    if (s === 1 || s === 4)
      return { kind: 'siteCost', value: vc.siteCost, label: 'Solicitado', status: s };
  }
  return { kind: 'reference', value: referenceCost, label: 'Referencia' };
};

/** Returns true if this concept was accepted or rejected in any version BEFORE versionIndex */
const isConceptFinalizedBefore = (
  versions: ContractNegotiationVersion[],
  negotiationConceptId: string,
  versionIndex: number
): boolean => {
  for (let i = 0; i < versionIndex; i++) {
    const s = getVersionConcept(versions[i], negotiationConceptId)?.negotiationConceptStatus;
    if (s === 2 || s === 3) return true;
  }
  return false;
};

const getConceptName = (
  negotiationConcept: ContractNegotiationConcept,
  allConcepts: Concept[]
): string => {
  const concept = allConcepts.find((c) => c.id === negotiationConcept.conceptId);
  return concept?.conceptName ?? String(negotiationConcept.conceptId);
};

const NegotiationConceptGridComponent = (props: Props) => {
  const { t } = useTranslation();
  const styles = useNegotiationConceptGridStyles();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [newVersionDate, setNewVersionDate] = useState<string>(
    new Date().toISOString().slice(0, 10)
  );
  const [conceptInputs, setConceptInputs] = useState<VersionConceptInputs>({});
  const [pendingValues, setPendingValues] = useState<PendingConceptValue[]>([]);
  const [isCreatingVersion, setIsCreatingVersion] = useState<boolean>(false);

  const [editingNcId, setEditingNcId] = useState<string | null>(null);
  const [editRefValue, setEditRefValue] = useState<string>('0');
  const [editNcCurrencyCode, setEditNcCurrencyCode] = useState<string>('');

  const prevVersionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    props.GetContractNegotiationConcepts(props.contractNegotiationId, props.contractId);
    props.GetContractNegotiationVersions(props.contractNegotiationId, props.contractId);
    props.GetAllConcepts();
    props.GetAllConceptCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contractNegotiationId, props.contractId]);

  useEffect(() => {
    // Skip auto-loading while we are in the middle of creating concepts for a new
    // version. The CREATE_RECEIVE actions will populate the concepts array without
    // the race condition of a GET overwriting them with a stale empty list.
    if (isCreatingVersion) return;
    for (const version of props.versions) {
      if (
        version.concepts === undefined &&
        !version.isGettingVersionConcepts &&
        !version.FailGettingVersionConcepts
      ) {
        props.GetContractNegotiationVersionConcepts(
          version.id,
          props.contractNegotiationId,
          props.contractId
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.versions]);

  useEffect(() => {
    if (!isCreatingVersion || pendingValues.length === 0) {
      prevVersionIdsRef.current = new Set(props.versions.map((v) => v.id));
      return;
    }

    const newVersions = props.versions.filter(
      (v) => !prevVersionIdsRef.current.has(v.id)
    );

    if (newVersions.length > 0) {
      const newVersion = newVersions[0];
      for (const pv of pendingValues) {
        const entity: NewContractNegotiationVersionConcept = {
          contractNegotiationVersionId: newVersion.id,
          contractNegotiationConceptId: pv.conceptId,
          sponsorCost: pv.sponsorCost,
          sponsorReferenceVariance: 0,
          siteCost: pv.siteCost,
          siteReferenceVariance: 0,
          negotiationConceptStatus: 4, // solicited (default)
        };
        props.CreateContractNegotiationVersionConcept(
          entity,
          props.contractId,
          props.contractNegotiationId
        );
      }
      setPendingValues([]);
      setIsCreatingVersion(false);
    }

    prevVersionIdsRef.current = new Set(props.versions.map((v) => v.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.versions]);

  const handleOpenDrawer = () => {
    const initialInputs: VersionConceptInputs = {};
    for (const nc of props.negotiationConcepts) {
      initialInputs[nc.id] = { sponsorCost: 0, siteCost: 0 };
    }
    setConceptInputs(initialInputs);
    setNewVersionDate(new Date().toISOString().slice(0, 10));
    setIsDrawerOpen(true);
  };

  const handleDismissDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleConceptInputChange = useCallback(
    (
      conceptId: string,
      field: 'sponsorCost' | 'siteCost',
      value: string
    ) => {
      const numeric = Number.parseFloat(value) || 0;
      setConceptInputs((prev) => ({
        ...prev,
        [conceptId]: {
          ...prev[conceptId],
          [field]: numeric,
        },
      }));
    },
    []
  );

  const handleSaveVersion = () => {
    prevVersionIdsRef.current = new Set(props.versions.map((v) => v.id));

    // Exclude concepts already finalized (accepted/rejected) in any existing version
    const pending: PendingConceptValue[] = props.negotiationConcepts
      .filter((nc) => !isConceptFinalizedBefore(props.versions, nc.id, props.versions.length))
      .map((nc) => ({
        conceptId: nc.id,
        sponsorCost: conceptInputs[nc.id]?.sponsorCost ?? 0,
        siteCost: conceptInputs[nc.id]?.siteCost ?? 0,
      }));

    const newVersion: NewContractNegotiationVersion = {
      contractNegotiationId: props.contractNegotiationId,
      negotiationDate: newVersionDate ? new Date(newVersionDate) : undefined,
    };

    setPendingValues(pending);
    setIsCreatingVersion(true);
    props.CreateContractNegotiationVersion(newVersion, props.contractId);
    setIsDrawerOpen(false);
  };

  const isLoading = props.isLoadingConcepts || props.isLoadingVersions;

  const categoryGroups = buildCategoryGroups(
    props.negotiationConcepts,
    props.allConcepts,
    props.conceptCategories,
    t
  );

  const totalVersionColumns = props.versions.length * 2;

  return (
    <div className={styles.root}>
      <NegotiationConceptGraphComponent
        negotiationConcepts={props.negotiationConcepts}
        versions={props.versions}
        allConcepts={props.allConcepts}
      />
      <div className={styles.toolbar}>
        <Text weight="semibold" size={400}>
          {t('negotiationConceptGrid.title', 'Grilla de negociación por concepto')}
        </Text>
        <Button
          appearance="primary"
          size="small"
          icon={<Add24Regular />}
          onClick={handleOpenDrawer}
          disabled={props.negotiationConcepts.length === 0}
        >
          {t('negotiationConceptGrid.addVersion', 'Nueva versión')}
        </Button>
      </div>

      {isLoading && (
        <div className={styles.spinnerWrapper}>
          <Spinner
            size="small"
            label={t('negotiationConceptGrid.loading', 'Cargando datos de negociación...')}
          />
        </div>
      )}

      {!isLoading && props.negotiationConcepts.length === 0 && (
        <div className={styles.emptyState}>
          <Text>{t('negotiationConceptGrid.empty', 'No hay conceptos asociados a esta negociación.')}</Text>
        </div>
      )}

      {!isLoading && props.negotiationConcepts.length > 0 && (
        <div className={styles.scrollWrapper}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th
                  className={styles.th}
                  rowSpan={2}
                >
                  {t('negotiationConceptGrid.col.concept', 'Concepto')}
                </th>
                <th
                  className={styles.th}
                  rowSpan={2}
                >
                  {t('negotiationConceptGrid.col.reference', 'Precio referencia')}
                </th>
                {props.versions.map((version, idx) => (
                  <th
                    key={version.id}
                    className={styles.thVersion}
                    colSpan={2}
                  >
                    {t('negotiationConceptGrid.versionLabel', 'V{{n}}', {
                      n: idx + 1,
                    })}{' '}
                    — {formatDate(version.negotiationDate)}
                  </th>
                ))}
                {totalVersionColumns === 0 && (
                  <th className={styles.th}>
                    {t('negotiationConceptGrid.noVersions', 'Sin versiones')}
                  </th>
                )}
                <th
                  className={styles.th}
                  rowSpan={2}
                  style={{ borderLeft: '2px solid var(--colorNeutralStroke1)', whiteSpace: 'nowrap' }}
                >
                  {t('negotiationConceptGrid.col.finalValue', 'Valor final')}
                </th>
              </tr>
              {props.versions.length > 0 && (
                <tr>
                  {props.versions.map((version) => (
                    <React.Fragment key={version.id}>
                      <th className={styles.thVersionSubFirst}>
                        {t('negotiationConceptGrid.col.sponsorOffer', 'Oferta sponsor')}
                      </th>
                      <th className={styles.thVersionSub}>
                        {t('negotiationConceptGrid.col.siteCounterOffer', 'Contraoferta')}
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {categoryGroups.map((group) => (
                <React.Fragment key={group.categoryId ?? UNCATEGORIZED_KEY}>
                  <tr className={styles.categoryRow}>
                    <td
                      className={styles.categoryCell}
                      colSpan={2 + Math.max(totalVersionColumns, 1) + 1}
                    >
                      {group.categoryName}
                    </td>
                  </tr>
                  {group.concepts.map((nc) => (
                    <tr key={nc.id}>
                      <td className={styles.td}>
                        {getConceptName(nc, props.allConcepts)}
                      </td>
                      <td className={styles.td} style={{ padding: 0 }}>
                        <Popover
                          withArrow
                          open={editingNcId === nc.id}
                          onOpenChange={(_, data) => {
                            if (data.open) {
                              setEditingNcId(nc.id);
                              setEditRefValue(String(nc.negotiationReferenceCost));
                              setEditNcCurrencyCode(nc.currencyCode || '');
                            } else {
                              setEditingNcId(null);
                            }
                          }}
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            {nc.negotiationReferenceCost || nc.currencyCode ? (
                              <button
                                className={styles.cellButton}
                                title={t('negotiationConceptGrid.editRefCostTooltip', 'Editar precio de referencia')}
                              >
                                <span>
                                  {formatCurrency(nc.negotiationReferenceCost)}
                                  {nc.currencyCode ? ` (${nc.currencyCode})` : ''}
                                </span>
                                <Edit16Regular style={{ opacity: 0.5, fontSize: 12, flexShrink: 0 }} />
                              </button>
                            ) : (
                              <button
                                className={styles.cellButton}
                                title={t('negotiationConceptGrid.addRefCostTooltip', 'Agregar precio de referencia')}
                              >
                                <span className={styles.emptyCell}>-</span>
                                <Add16Regular style={{ opacity: 0.5, fontSize: 12, flexShrink: 0 }} />
                              </button>
                            )}
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            aria-label={t('negotiationConceptGrid.editRefCostLabel', 'Editar precio de referencia')}
                          >
                            <div style={{ minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
                              <Text weight="semibold" size={300}>
                                {t('negotiationConceptGrid.editRefCostTitle', 'Precio de referencia')}
                              </Text>
                              <Field label={t('negotiationConceptGrid.refCost', 'Costo')}>
                                <Input
                                  type="number"
                                  step={0.01}
                                  value={editRefValue}
                                  onChange={(_, data) => setEditRefValue(data.value)}
                                />
                              </Field>
                              <CurrencySelectorComponent
                                label={t('negotiationConceptGrid.currency', 'Moneda')}
                                defaultSelected={editNcCurrencyCode || undefined}
                                errorMessage={undefined}
                                onCurrencyChange={(code) => setEditNcCurrencyCode(code)}
                              />
                              <Button
                                appearance="primary"
                                size="small"
                                onClick={() => {
                                  props.UpdateContractNegotiationConcept(
                                    {
                                      ...nc,
                                      negotiationReferenceCost: Number.parseFloat(editRefValue) || 0,
                                      currencyCode: editNcCurrencyCode || undefined,
                                    },
                                    props.contractId
                                  );
                                  setEditingNcId(null);
                                }}
                              >
                                {t('negotiationConceptGrid.save', 'Guardar')}
                              </Button>
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </td>
                      {props.versions.map((version, vIdx) => {
                        const vc = getVersionConcept(version, nc.id);
                        const finalized = isConceptFinalizedBefore(props.versions, nc.id, vIdx);
                        return (
                          <VersionConceptCellConnected
                            key={version.id}
                            versionConcept={vc}
                            versionId={version.id}
                            negotiationConceptId={nc.id}
                            currencyCode={nc.currencyCode}
                            contractId={props.contractId}
                            contractNegotiationId={props.contractNegotiationId}
                            isConceptFinalized={finalized}
                          />
                        );
                      })}
                      {totalVersionColumns === 0 && (
                        <td className={styles.td}>
                          <span className={styles.emptyCell}>-</span>
                        </td>
                      )}
                      {(() => {
                        const fv = getFinalValue(props.versions, nc.id, nc.negotiationReferenceCost);
                        let bg = 'transparent';
                        if (fv.kind === 'reference') bg = '#9e9e9e';
                        else if (fv.kind === 'siteCost' && (fv.status === 1 || fv.status === 4)) bg = '#f9a825';
                        else if (fv.kind === 'siteCost' && fv.status === 2) bg = '#2e7d32';
                        else if (fv.kind === 'sponsorCost' && fv.status === 2) bg = '#2e7d32'; // accepted sponsor offer, no counter-offer
                        else if (fv.kind === 'sponsorCost') bg = '#c62828';
                        const isDark = fv.kind === 'reference' || (fv.kind === 'siteCost' && fv.status === 2) || (fv.kind === 'sponsorCost' && fv.status === 2) || fv.kind === 'sponsorCost';
                        const color = isDark ? '#fff' : '#000';

                        const refCost = nc.negotiationReferenceCost;
                        let variationBadge: React.ReactNode = null;
                        if (fv.kind !== 'noData' && fv.kind !== 'reference' && refCost > 0) {
                          const pct = ((fv.value - refCost) / refCost) * 100;
                          const sign = pct >= 0 ? '+' : '';
                          variationBadge = (
                            <span style={{
                              display: 'inline-block',
                              marginTop: 2,
                              padding: '1px 6px',
                              borderRadius: 10,
                              fontSize: 10,
                              fontWeight: 700,
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              color,
                              whiteSpace: 'nowrap',
                            }}>
                              {sign}{pct.toFixed(1)}%
                            </span>
                          );
                        }

                        return (
                          <td
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid var(--colorNeutralStroke2)',
                              borderLeft: '2px solid var(--colorNeutralStroke1)',
                              whiteSpace: 'nowrap',
                              backgroundColor: bg,
                              color,
                              fontWeight: 600,
                            }}
                          >
                            {fv.kind === 'noData' ? (
                              <span>-</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                                <span>{formatCurrency(fv.value)}{nc.currencyCode ? ` ${nc.currencyCode}` : ''}</span>
                                {variationBadge}
                              </div>
                            )}
                          </td>
                        );
                      })()}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OverlayDrawer
        position="end"
        size="medium"
        open={isDrawerOpen}
        onOpenChange={(_, data) => {
          if (!data.open) handleDismissDrawer();
        }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('negotiationConceptGrid.drawer.close', 'Cerrar')}
                icon={<DismissRegular />}
                onClick={handleDismissDrawer}
              />
            }
          >
            {t('negotiationConceptGrid.drawer.title', 'Nueva versión de negociación')}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className={styles.drawerBody}>
            <Field
              label={t('negotiationConceptGrid.drawer.dateLabel', 'Fecha de negociación')}
              required
            >
              <Input
                type="date"
                value={newVersionDate}
                onChange={(_, data) => setNewVersionDate(data.value)}
              />
            </Field>

            <Text weight="semibold" size={300}>
              {t(
                'negotiationConceptGrid.drawer.conceptsTitle',
                'Valores por concepto'
              )}
            </Text>

            {props.negotiationConcepts.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table className={styles.drawerConceptsTable}>
                  <thead>
                    <tr>
                      <th className={styles.drawerTh}>
                        {t('negotiationConceptGrid.drawer.col.concept', 'Concepto')}
                      </th>
                      <th className={styles.drawerTh}>
                        {t('negotiationConceptGrid.drawer.col.reference', 'Referencia')}
                      </th>
                      <th className={styles.drawerTh}>
                        {t('negotiationConceptGrid.drawer.col.sponsorOffer', 'Oferta sponsor')}
                      </th>
                      <th className={styles.drawerTh}>
                        {t('negotiationConceptGrid.drawer.col.counterOffer', 'Contraoferta')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.negotiationConcepts.map((nc) => {
                      const isFinalized = isConceptFinalizedBefore(props.versions, nc.id, props.versions.length);
                      if (isFinalized) {
                        const fv = getFinalValue(props.versions, nc.id, nc.negotiationReferenceCost);
                        const finalValue = fv.kind !== 'noData' && fv.kind !== 'reference'
                          ? formatCurrency(fv.value)
                          : '-';
                        const isAccepted = (fv.kind === 'siteCost' || fv.kind === 'sponsorCost') && fv.status === 2;
                        const finalLabel = isAccepted
                          ? t('negotiationConceptGrid.drawer.accepted', 'Aceptado')
                          : t('negotiationConceptGrid.drawer.rejected', 'Rechazado');
                        const badgeBg = isAccepted ? '#2e7d32' : '#c62828';
                        return (
                          <tr key={nc.id} style={{ opacity: 0.5 }}>
                            <td className={styles.drawerTd}>
                              {getConceptName(nc, props.allConcepts)}
                            </td>
                            <td className={styles.drawerTd}>
                              {formatCurrency(nc.negotiationReferenceCost)}
                            </td>
                            <td className={styles.drawerTd} colSpan={2}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '2px 8px',
                                borderRadius: 10,
                                backgroundColor: badgeBg,
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 600,
                              }}>
                                {finalLabel}: {finalValue}{nc.currencyCode ? ` ${nc.currencyCode}` : ''}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={nc.id}>
                          <td className={styles.drawerTd}>
                            {getConceptName(nc, props.allConcepts)}
                          </td>
                          <td className={styles.drawerTd}>
                            {formatCurrency(nc.negotiationReferenceCost)}
                          </td>
                          <td className={styles.drawerTd}>
                            <Input
                              className={styles.inputSmall}
                              type="number"
                              value={String(conceptInputs[nc.id]?.sponsorCost ?? 0)}
                              onChange={(_, data) =>
                                handleConceptInputChange(nc.id, 'sponsorCost', data.value)
                              }
                            />
                          </td>
                          <td className={styles.drawerTd}>
                            <Input
                              className={styles.inputSmall}
                              type="number"
                              value={String(conceptInputs[nc.id]?.siteCost ?? 0)}
                              onChange={(_, data) =>
                                handleConceptInputChange(nc.id, 'siteCost', data.value)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className={styles.drawerFooter}>
              <Button appearance="secondary" onClick={handleDismissDrawer}>
                {t('negotiationConceptGrid.drawer.cancel', 'Cancelar')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleSaveVersion}
                disabled={!newVersionDate}
              >
                {t('negotiationConceptGrid.drawer.save', 'Guardar versión')}
              </Button>
            </div>
          </div>
        </DrawerBody>
      </OverlayDrawer>
    </div>
  );
};

export { NegotiationConceptGridComponent };
export default connect(mapStateToProps, mapDispatchToProps)(
  NegotiationConceptGridComponent as React.ComponentType<OwnProps>
);
